import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, NavigationExtras, Router, RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';

import { Store } from '@ngrx/store';
import { updateSelectedCurrencyCode } from '@actions/culture.action';
import {
  appAbandonToken,
  appUpdateModifySearchWidget,
} from '@actions/app.action';
import {
  selectIsModifyAndPnrExists,
  selectShouldUseMobileStyling,
  selectShowModifySearchWidget,
  selectShowModifySearchNavbarWidget,
} from '@selectors/app.selector';
import { selectLoggedInUser } from '@selectors/user.selector';
import { selectBookingCurrency } from '@selectors/booking.selector';
import { selectStationsState } from '@selectors/station.selector';
import {
  selectCurrentCultureName,
  selectCurrentCultureShortName,
  selectSelectedCurrencyCode,
  selectSelectedCurrencyPrefix,
  selectSelectedCurrencyPrefixIncludeAll,
  selectSelectedCurrencySuffix,
} from '@selectors/culture.selector';
import {
  selectCountries,
  selectCultures,
  selectCurrencies,
  selectHeaderOrderedCultures,
} from '@selectors/resource.selector';
import {
  selectSearchInputState,
  selectAvailabilityTripTypeState,
} from '@selectors/availability.selector';

import { LoginModalComponent } from '@components/modals/login-modal/login-modal.component';
import { SearchWidgetComponent } from '@components/search-widget/search-widget.component';
import { ModifySearchCriteriaComponent } from '@components/modify-search-criteria/modify-search-criteria.component';
import { LinkDisplayComponent } from '@components/link-display/link-display.component';

import { LetDirective } from '@directives/app-let.directive';
import {
  CulturePipe,
  CulturePipeTransformType,
} from '@pipes/culture/culture.pipe';

import { ModalService } from '@services/modal/modal.service';
import { GeocodeService } from '@services/geocode/geocode.service';

import { HeaderCopy } from '@models/cms/header.model';
import { SearchRequest } from '@models/requests/search-request.model';
import { CountryResource } from '@models/resources/country.model';
import { TripType } from '@constants/home.constants';
import {
  FlowBaseUrlEnum,
  FALLBACK_CULTURE,
  CultureKeyType,
  CULTURE_ROUTE_PARAM,
  SECONDS_PER_DAY, CULTURE_US, CULTURE_FRENCH,
} from '@constants/app.constants';
import { DATE_FORMAT_DAY_SPACE_SHORT_MONTH_NAME } from '@constants/date-format.constants';
import { AppCopy } from '@models/cms/app.model';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { NullableString } from '@models/app.models';
import { CmsSubKeys } from '@constants/cms.constants';
import {
  selectIsBookPath,
  selectIsFlightsPage,
  selectIsFlowPage,
  selectIsHomePage,
  selectIsSupportPage,
  selectIsUpgradePath,
  selectQueryParams,
  selectRouteParam,
} from '@selectors/router.selector';
import { concatLatestFrom } from '@ngrx/effects';
import { FeatureFlagsEnum } from '@models/environment.type';
import { CookieStorageService } from '@services/cookie-storage/cookie-storage.service';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { UtilitiesService } from '@services/utilities/utilities.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { HeaderMenuUserComponent } from './header-menu/header-menu-user/header-menu-user.component';
import { CurrencyResource } from '@models/resources/currency.model';
import { TooltipDirective, TooltipModule } from 'ngx-bootstrap/tooltip';
import { CultureResource } from '@models/resources/culture.model';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { TopMenuComponent } from './top-menu/top-menu.component';
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { TopMenuMobileComponent } from "./top-menu/top-menu-mobile/top-menu-mobile.component";
import { PersonName } from '@models/user.model';
import {CultureDropdownComponent} from "@components/culture-dropdown/culture-dropdown.component";
import { selectNavBarCopy } from '@selectors/cms.selector';
import { TopNavbarComponent } from './top-navbar/top-navbar.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    LetDirective,
    CulturePipe,
    ModalModule,
    CollapseModule,
    LoginModalComponent,
    RouterModule,
    ReactiveFormsModule,
    SearchWidgetComponent,
    ModifySearchCriteriaComponent,
    LinkDisplayComponent,
    AccordionModule,
    HeaderMenuUserComponent,
    TooltipModule,
    NgClickOutsideDirective,
    TopMenuComponent,
    SvgIconComponent,
    TopMenuMobileComponent,
    CultureDropdownComponent,
      TopNavbarComponent
],
  providers: [GeocodeService],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() cmsCopy: HeaderCopy;
  @Input() cmsAppCopy: AppCopy;

  @Output() scrollTop = new EventEmitter<void>();

  @ViewChild('navbarSupportedContent', { static: false })
  navbarSupportedContent: ElementRef<HTMLElement>;
  @ViewChild('burgerInput', { static: false })
  burgerInput: ElementRef<HTMLElement>;
  @ViewChild('currencyTooltip', { static: false }) currencyTooltip: TooltipDirective;

  onDestroy$ = new Subject<void>();
  cultureName$ = this.store.select(selectCurrentCultureName);
  cultureShortName$ = this.store.select(selectCurrentCultureShortName);
  currencies$ = this.store.select(selectCurrencies);
  selectedCurrency$ = this.store.select(selectSelectedCurrencyCode);
  selectedCurrencyPrefix$ = this.store.select(selectSelectedCurrencyPrefix);
  selectedCurrencySuffix$ = this.store.select(selectSelectedCurrencySuffix);
  selectedCurrencyPrefixIncludeAll$ = this.store.select(selectSelectedCurrencyPrefixIncludeAll);
  countries$ = this.store.select(selectCountries);
  stations$ = this.store.select(selectStationsState);
  bookingCurrency$ = this.store.select(selectBookingCurrency);
  geoOriginCountry: CountryResource | undefined;
  searchInput$ = this.store.select(selectSearchInputState);
  tripType$ = this.store.select(selectAvailabilityTripTypeState);
  loggedInUser$ = this.store.select(selectLoggedInUser);
  getShowModifySearchWidget$ = this.store.select(selectShowModifySearchWidget);
  getShowModifySearchNavbarWidget$ = this.store.select(
    selectShowModifySearchNavbarWidget,
  );
  culture$ = this.store.select(
    selectRouteParam(CULTURE_ROUTE_PARAM),
  ) as Observable<CultureKeyType | undefined>;
  isBookingPath$ = this.store.select(selectIsBookPath);
  isModifyPath$ = this.store.select(selectIsModifyAndPnrExists);
  isUpgradePath$ = this.store.select(selectIsUpgradePath);
  isFlowPage$ = this.store.select(selectIsFlowPage);
  isFlightsPage$ = this.store.select(selectIsFlightsPage);
  isHomePage$ = this.store.select(selectIsHomePage);
  isSupportPage$ = this.store.select(selectIsSupportPage);
  mobileStyle$ = this.store.select(selectShouldUseMobileStyling);
  queryParams$ = this.store.select(selectQueryParams);

  travelerCopy = '';
  travelersCopy = '';
  modalRef?: BsModalRef<LoginModalComponent>;
  countries: CountryResource[] | null = null;
  // TODO: allow toggle on header dropdowns
  collapseCultures = true;
  collapseLogout = true;
  collapseCurrencies = true;
  collapseNavigation = true;

  readonly FALLBACK_CULTURE = FALLBACK_CULTURE;
  readonly CmsSubKeys = CmsSubKeys;
  readonly CulturePipeTransformType = CulturePipeTransformType;
  readonly FeatureFlagsEnum = FeatureFlagsEnum;
  readonly FlowBaseUrlEnum = FlowBaseUrlEnum;
  readonly GrowthBookFeatureKeys = GrowthBookFeatureKeys;

  public navbarCopy$ = this.store.select(selectNavBarCopy);

  get is1433TopMenuReorderingEnabled(): boolean {
    return this.gbs.isVariantOfExperimentEnabled('feat-001433-top-menu-reordering', 'b');
  }

  get is3322TopMenuReorderingEnabled(): boolean {
    const featureKey = 'feat-003322-your-trip-navigation-bar-placement';
    return (
      !this.gbs.isVariantOfExperimentEnabled(featureKey, 'a') &&
      this.gbs.growthBook.isOn(featureKey)
    );
  }

  constructor(
    public modalService: ModalService,
    public store: Store,
    public featureFlagService: FeatureFlagService,
    private router: Router,
    private cookieStorageService: CookieStorageService,
    private cd: ChangeDetectorRef,
    private utilitiesService: UtilitiesService,
    public gbs: GrowthBookService,
  ) {
  }

  ngOnInit(): void {
    this.gbs.onChange$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.cd.detectChanges();
    });

    ({ traveler: this.travelerCopy, travelers: this.travelersCopy } =
      this.cmsCopy);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        concatLatestFrom(() => [
          this.getShowModifySearchWidget$,
          this.isFlightsPage$,
          this.queryParams$,
        ]),
        takeUntil(this.onDestroy$),
      )
      .subscribe(([, showSearchWidget, isFlightsPage, queryParams]) => {
        // bypassing flights page here to allow autosearching to show/hide widget
        if (showSearchWidget && !isFlightsPage) {
          this.store.dispatch(
            appUpdateModifySearchWidget({
              showWidget: false,
              showLink: true,
            }),
          );
        }

        if (isFlightsPage && queryParams) {
          let extras: NavigationExtras = {
            replaceUrl: true,
          };
          if (!this.gbs.growthBook.getFeatureValue('feat-002420-blank-page-on-flight-impact', true)) {
            extras = {
              ...extras,
              queryParamsHandling: 'preserve',
            };
          }
          this.router.navigate([], extras);
        }
      });
    combineLatest([
      this.isHomePage$,
      this.selectedCurrency$,
      this.bookingCurrency$,
      this.isFlightsPage$,
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        ([isHomePage, selectedCurrency, bookingCurrency, isFlightsPage]) => {
          if (!isHomePage && bookingCurrency) {
            this.changeCurrency(
              bookingCurrency,
              isFlightsPage,
              selectedCurrency,
            );
          }
        },
      );

    this.utilitiesService.documentClickedTarget
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((target: HTMLElement) => this.documentClickListener(target));

    this.hideMobileMenuOnChangeRoute();
  }

  documentClickListener(target: EventTarget | null): void {
    if (
      target instanceof HTMLElement &&
      !this.navbarSupportedContent?.nativeElement.contains(target) &&
      !this.burgerInput?.nativeElement.contains(target)
    ) {
      this.collapseNavigation = true;
      this.cd.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    // need to detect changes here to prevent change after checked
    // when using the deeplink to flights with a booking in state
    this.bookingCurrency$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.cd.detectChanges());
  }

  openLoginModal(): void {
    this.collapseNavigation = true;
    this.modalRef = this.modalService.show(LoginModalComponent, {
      initialState: {
        loginCopy: this.cmsAppCopy[CmsSubKeys.login],
        tabs: this.cmsAppCopy.tabs,
        passwordHints: this.cmsAppCopy.passwordHints[0].value,
      },
    });
  }

  changeCurrency(
    newCurrency: string,
    isFlightsPage: boolean,
    selectedCurrency: NullableString,
  ): void {
    if (newCurrency === selectedCurrency) {
      return;
    }
    this.collapseCurrencies = true;
    this.store.dispatch(
      updateSelectedCurrencyCode({ code: newCurrency, isFlightsPage }),
    );
  }

  onModifyClick(): void {
    this.store.dispatch(
      appUpdateModifySearchWidget({
        showWidget: true,
        showLink: false,
      }),
    );
  }

  onBookClick(withScroll?: boolean): void {
    if (this.cmsCopy.book.link) {
      this.router.navigate([this.cmsCopy.book.link]);
      this.collapseNavigation = true;
      if (withScroll) {
        this.scrollTop.emit();
      }
    }
  }

  getselectedStations(
    searchRequest: SearchRequest | null,
    tripType: TripType | null,
  ): string {
    if (!searchRequest || searchRequest.criteria.length === 0) {
      return '';
    }

    switch (tripType) {
      case TripType.multiCity:
        return searchRequest.criteria
          .map((s) => `${s?.origin} - ${s?.destination}`)
          ?.reduce((a, c) => `${a} ${c}`)
          .trim()
          .replace('JM1', 'ALL')
          .replace('FL1', 'ALL')
          .replace('SF1', 'ALL');
      case TripType.roundTrip || TripType.oneWay:
        return `${searchRequest.criteria[0]?.origin} - ${searchRequest.criteria[0]?.destination}`
          .replace('JM1', 'ALL')
          .replace('FL1', 'ALL')
          .replace('SF1', 'ALL');
      default:
        return '';
    }
  }

  getselectedDates(
    searchRequest: SearchRequest | null,
    tripType: TripType | null,
    culture: string,
  ): string {
    if (!searchRequest || searchRequest.criteria.length === 0) {
      return '';
    }

    switch (tripType) {
      case TripType.oneWay:
        return `${DateTime.fromISO(
          searchRequest.criteria[0]?.beginDate ?? DateTime.now().toISO()!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        )?.toFormat(DATE_FORMAT_DAY_SPACE_SHORT_MONTH_NAME, {
          locale: culture,
        })}`;
      default:
        return (
          searchRequest.criteria
            .map(
              (s) =>
                `${DateTime.fromISO(s.beginDate)?.toFormat(
                  DATE_FORMAT_DAY_SPACE_SHORT_MONTH_NAME,
                  {
                    locale: culture,
                  },
                )}`,
            )
            ?.reduce((a, c) => `${a} - ${c}`)
            .trim() ?? ''
        );
    }
  }

  getselectedTravelerCount(searchRequest: SearchRequest | null): string {
    const travelerCount =
      (searchRequest?.passengers?.length ?? 0) === 0
        ? 0
        : searchRequest?.passengers
            ?.map((p) => p.count ?? 0)
            ?.reduce((a, c) => a + c, 0) ?? 0;
    return travelerCount > 1
      ? `${travelerCount} ${this.cmsCopy.travelers.toLocaleLowerCase()}`
      : `${travelerCount} ${this.cmsCopy.traveler.toLocaleLowerCase()}`;
  }

  getSelectedCurrencyWithPrefixSuffix(
    selectedCurrencyPrefix: string | null,
    selectedCurrencySuffix: string | null,
    selectedCurrency: string | null,
  ) {
    if (selectedCurrency) {
      return selectedCurrencyPrefix + selectedCurrency + selectedCurrencySuffix;
    }
    return '';
  }

  getSelectedCurrencyWithSuffix(
    selectedCurrencyPrefix: string | null,
    selectedCurrencySuffix: string | null,
    selectedCurrency: string | null,
  ) {
    if (selectedCurrency) {
      selectedCurrencyPrefix = selectedCurrencyPrefix ?? '';
      selectedCurrencySuffix = selectedCurrencySuffix ?? '';
      return `${selectedCurrency} (${selectedCurrencyPrefix + selectedCurrencySuffix})`;
    }
    return '';
  }

  getCurrencyWithSuffix(currency: CurrencyResource) {
      const currencyPrefix = currency.displayPrefix ?? '';
      const currencySuffix = currency.displaySuffix ?? '';
      return `${currency.currencyCode} (${currencyPrefix + currencySuffix})`;
    }


  /* istanbul ignore next */
  logOut(): void {
    this.collapseNavigation = true;
    this.store.dispatch(appAbandonToken());
  }

  hideTooltip(): void {
    this.currencyTooltip?.hide();
    this.collapseCurrencies = true
  }

  getInitialsFromUser(user?: PersonName): string {
    if (!user) {
      return '';
    }

    return user.first.charAt(0) + user.last.charAt(0);
  }

  private hideMobileMenuOnChangeRoute(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.setCollapseNavigation(true);
      });
  }

  private setCollapseNavigation(collased: boolean): void {
    this.collapseNavigation = collased;
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
